import { useState, useRef, useEffect } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import useAuthStore from '../../../store/authStore';
import DraggableBoardItem from './DraggableBoardItem';
import BoardItemMenu from './BoardItemMenu';
import WorkspaceFolderMenu from './WorkspaceFolderMenu';
import { dialog } from '../../../store/dialogStore';
import { canManageBoardEntry, canManageBoardProtection } from '../../../utils/permissions';

export default function WorkspaceFolder({ folder, boards, activeBoardId, onNavigate, canManage, selectionProps = {} }) {
  const user = useAuthStore((s) => s.user);
  const { selectable, anySelected, onToggleSelect, selectedIds } = selectionProps;
  const [expanded, setExpanded] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(folder.name);
  const [ctxPoint, setCtxPoint] = useState(null);
  const inputRef = useRef(null);

  const {
    renameWorkspaceFolder,
    deleteWorkspaceFolder,
    moveBoardToWorkspaceFolder,
    duplicateBoard,
    archiveBoard,
    fetchBoards,
  } = useBoardStore();

  // Droppable da pasta INTEIRA: alvo para soltar boards "dentro" (comportamento atual)
  // E tambem alvo de reordenacao da raiz quando o item arrastado e uma pasta.
  const { setNodeRef, isOver } = useDroppable({
    id: `ws-folder-${folder.id}`,
    data: { type: 'workspaceFolder', folderId: folder.id, workspaceId: folder.workspace_id },
  });

  // Pasta arrastavel (reordenar na lista unica raiz). Igual ao quadro: o header
  // INTEIRO vira handle (sem grip dedicado). O clique no chevron/nome continua
  // funcionando porque o PointerSensor usa activationConstraint distance:5 — clique
  // sem mover nao vira drag. Botao "..." e seletor bloqueiam o drag via stopPropagation.
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `folder-drag-${folder.id}`,
    data: { type: 'folder', folderId: folder.id, workspaceId: folder.workspace_id },
    disabled: !canManage || renaming,
  });

  // Hint de reordenacao da raiz quando o item arrastado e outra pasta/board e
  // o ponteiro esta sobre ESTA pasta. Le do store (setado em handleDragOver).
  const dropHint = useBoardStore(
    useShallow((s) => (s.sidebarDropHint?.folderId === folder.id ? s.sidebarDropHint : null))
  );
  const dragKind = useBoardStore((s) => s.sidebarDragKind);
  const showFolderBefore = dropHint?.position === 'before';
  const showFolderAfter = dropHint?.position === 'after';
  // "dentro" so destaca quando arrastando um BOARD (nao quando arrastando pasta)
  const showInside = isOver && dragKind === 'board';

  useEffect(() => { setName(folder.name); }, [folder.name]);
  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  const saveName = () => {
    setRenaming(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== folder.name) renameWorkspaceFolder(folder.id, trimmed);
    else setName(folder.name);
  };

  const handleDelete = async () => {
    if (await dialog.confirm(
      `Excluir pasta "${folder.name}"`,
      'Os quadros dentro da pasta continuam disponíveis na área de trabalho.',
      { confirmLabel: 'Excluir', danger: true }
    )) {
      deleteWorkspaceFolder(folder.id);
    }
  };

  const handleContextMenu = (e) => {
    if (!canManage) return;
    e.preventDefault();
    e.stopPropagation();
    setCtxPoint({ x: e.clientX, y: e.clientY });
  };

  return (
    <div
      ref={setNodeRef}
      className={`relative rounded transition-colors ${showInside ? 'bg-accent/10 ring-1 ring-accent/40' : ''}`}
    >
      {showFolderBefore && (
        <div className="mx-2 mb-0.5" aria-hidden="true">
          <div className="h-1 bg-accent rounded-full" />
        </div>
      )}

      <div style={isDragging ? { height: 0, overflow: 'hidden' } : undefined}>
      <div
        ref={setDragRef}
        {...(canManage ? attributes : {})}
        {...(canManage ? listeners : {})}
        onClick={() => { if (!renaming) setExpanded((v) => !v); }}
        onContextMenu={handleContextMenu}
        className={`group/f relative flex items-center gap-1 px-2 py-1 text-xs text-text-secondary select-none rounded ${canManage ? 'cursor-grab active:cursor-grabbing' : ''}`}
      >
        <span className="p-0.5 text-text-secondary group-hover/f:text-text-primary transition-colors">
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </span>
        <Folder className="w-3.5 h-3.5 text-accent/80 shrink-0" />
        {renaming ? (
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveName();
              if (e.key === 'Escape') { setName(folder.name); setRenaming(false); }
            }}
            className="flex-1 bg-transparent border border-accent/50 rounded px-1 text-xs text-text-primary outline-none"
          />
        ) : (
          <span className="flex-1 truncate font-medium">{folder.name}</span>
        )}
        {canManage && (
          <WorkspaceFolderMenu
            folder={folder}
            onRename={() => setRenaming(true)}
            onDelete={handleDelete}
          />
        )}
        {canManage && ctxPoint && (
          <WorkspaceFolderMenu
            folder={folder}
            onRename={() => setRenaming(true)}
            onDelete={handleDelete}
            anchorPoint={ctxPoint}
            onClose={() => setCtxPoint(null)}
            hideButton
          />
        )}
      </div>

      {expanded && (
        <div className="ml-3 mr-2 mt-0.5 mb-1 border border-white/10 rounded-md px-1 py-1 bg-white/[0.02] space-y-0.5">
          {boards.length === 0 ? (
            <div className="pl-3 pr-4 py-1 text-[11px] text-text-secondary/60 italic">
              Arraste um board aqui
            </div>
          ) : (
            boards.map((b) => {
              // Menu de opcoes (Renomear/Mover/Duplicar/Excluir=arquivar) por
              // board: dono do quadro (my_level==='owner') ou admin da org.
              // Fallback sem my_level (backend M1a) = regra antiga da pasta
              // (canManage, so isOrgAdmin).
              // Mesma divisao de AppLayout: o menu abre tambem para quem so
              // pode definir a senha de abertura (dono nao-admin do quadro);
              // "Remover da pasta", duplicar e excluir ficam so para quem
              // gerencia o quadro, e o BoardItemMenu esconde renomear/mover.
              const canManageThisBoard = canManageBoardEntry(b, user);
              const showBoardMenu = canManageThisBoard || canManageBoardProtection(b, user);
              const menuProps = canManageThisBoard ? {
                extraItems: [
                  {
                    label: 'Remover da pasta',
                    onClick: () => moveBoardToWorkspaceFolder(b.id, null),
                  },
                ],
                onDuplicate: async () => {
                  const dup = await duplicateBoard(b.id);
                  if (dup) {
                    await fetchBoards(folder.workspace_id);
                    onNavigate(`/board/${dup.id}`);
                  }
                },
                onDelete: async () => {
                  if (await dialog.confirm('Mover para a lixeira', `Mover "${b.name}" para a lixeira?`, { confirmLabel: 'Mover', danger: true })) {
                    await archiveBoard(b.id);
                    await fetchBoards(folder.workspace_id);
                    if (b.id === activeBoardId) onNavigate('/');
                  }
                },
              } : {};
              return (
                <DraggableBoardItem
                  key={`wsf-${folder.id}-${b.id}`}
                  board={b}
                  active={b.id === activeBoardId}
                  onClick={() => onNavigate(`/board/${b.id}`)}
                  sourceWsFolderId={folder.id}
                  indent={0}
                  selectable={selectable}
                  anySelected={anySelected}
                  onToggleSelect={onToggleSelect}
                  selected={selectedIds?.has(b.id)}
                  renderContextMenu={showBoardMenu ? ({ anchorPoint, onClose }) => (
                    <BoardItemMenu board={b} {...menuProps} anchorPoint={anchorPoint} onClose={onClose} hideButton />
                  ) : undefined}
                >
                  {showBoardMenu && !anySelected && (
                    <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                      <BoardItemMenu board={b} {...menuProps} />
                    </div>
                  )}
                </DraggableBoardItem>
              );
            })
          )}
        </div>
      )}
      </div>

      {showFolderAfter && (
        <div className="mx-2 mt-0.5" aria-hidden="true">
          <div className="h-1 bg-accent rounded-full" />
        </div>
      )}
    </div>
  );
}
